import api from './api';

// ============================================
// Types
// ============================================

export interface CashbackDashboard {
  total_emitido: number;
  total_utilizado: number;
  total_expirado: number;
  total_pendente: number;
  total_confirmado: number;
  custo_pagali: number;
  custo_comerciante: number;
  total_users: number;
  campanhas_ativas: number;
  total_campanhas: number;
}

export interface CashbackCampaign {
  id: number;
  name: string;
  description: string | null;
  tipo: 'PERCENTUAL' | 'VALOR_FIXO' | 'PRIMEIRA_COMPRA';
  percentagem: number | null;
  valor_fixo: number | null;
  max_por_transacao: number | null;
  max_por_utilizador: number | null;
  max_utilizacoes_por_user: number | null;
  start_date: string;
  end_date: string;
  status: 'RASCUNHO' | 'ATIVA' | 'PAUSADA' | 'TERMINADA' | 'ESGOTADA';
  budget_total: number;
  budget_used: number;
  funding_source: 'PAGALI' | 'COMERCIANTE' | 'PARTILHADO';
  funding_split_pagali: number;
  funding_split_merchant: number;
  eligible_merchants: number[] | null;
  eligible_categories: string[] | null;
  restricao_uso: 'LIVRE' | 'RESTRITA';
  restricao_detalhes: any;
  created_by: string;
  created_at: string;
  stats?: {
    total_cashbacks: number;
    confirmados: number;
    utilizados: number;
    expirados: number;
    cancelados: number;
    total_valor: number;
    total_users: number;
  };
}

export interface CashbackRecord {
  id: number;
  user_id: string;
  merchant_id: number | null;
  transaction_id: number | null;
  campaign_id: number;
  campaign_name: string | null;
  valor_transacao: number;
  percentagem: number | null;
  valor_cashback: number;
  origem_financiamento: string;
  valor_pagali: number;
  valor_comerciante: number;
  estado: string;
  created_at: string;
  confirmed_at: string | null;
  used_at: string | null;
  expired_at: string | null;
}

export interface CampaignFormData {
  name: string;
  description?: string;
  tipo: string;
  percentagem?: number;
  valor_fixo?: number;
  max_por_transacao?: number;
  max_por_utilizador?: number;
  max_utilizacoes_por_user?: number;
  start_date: string;
  end_date: string;
  budget_total: number;
  funding_source: string;
  funding_split_pagali?: number;
  funding_split_merchant?: number;
  eligible_merchants?: number[];
  eligible_categories?: string[];
  restricao_uso?: string;
  restricao_detalhes?: any;
}

// ============================================
// Service
// ============================================

class CashbackService {
  async getDashboard(): Promise<CashbackDashboard> {
    const res = await api.get<{ success: boolean; data?: CashbackDashboard }>('/admin/cashback/dashboard');
    return res.data.data || {
      total_emitido: 0, total_utilizado: 0, total_expirado: 0, total_pendente: 0,
      total_confirmado: 0, custo_pagali: 0, custo_comerciante: 0,
      total_users: 0, campanhas_ativas: 0, total_campanhas: 0,
    };
  }

  async listCampaigns(params?: {
    status?: string;
    funding_source?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: CashbackCampaign[]; pagination: { total: number } }> {
    const qs = new URLSearchParams();
    if (params?.status) qs.append('status', params.status);
    if (params?.funding_source) qs.append('funding_source', params.funding_source);
    if (params?.search) qs.append('search', params.search);
    if (params?.limit) qs.append('limit', String(params.limit));
    if (params?.offset) qs.append('offset', String(params.offset));
    const url = `/admin/cashback/campaigns${qs.toString() ? `?${qs}` : ''}`;
    const res = await api.get<{ success: boolean; data: CashbackCampaign[]; pagination: { total: number } }>(url);
    return { data: res.data.data || [], pagination: res.data.pagination || { total: 0 } };
  }

  async getCampaign(id: number): Promise<CashbackCampaign | null> {
    const res = await api.get<{ success: boolean; data?: CashbackCampaign }>(`/admin/cashback/campaigns/${id}`);
    return res.data.data || null;
  }

  async createCampaign(data: CampaignFormData): Promise<{ id: number }> {
    const res = await api.post<{ success: boolean; data?: { id: number }; error?: string }>('/admin/cashback/campaigns', data);
    if (!res.data.success) throw new Error(res.data.error || 'Erro ao criar campanha');
    return res.data.data || { id: 0 };
  }

  async updateCampaign(id: number, data: Partial<CampaignFormData>): Promise<void> {
    const res = await api.put<{ success: boolean; error?: string }>(`/admin/cashback/campaigns/${id}`, data);
    if (!res.data.success) throw new Error(res.data.error || 'Erro ao atualizar campanha');
  }

  async updateCampaignStatus(id: number, status: string): Promise<void> {
    const res = await api.put<{ success: boolean; error?: string }>(`/admin/cashback/campaigns/${id}/status`, { status });
    if (!res.data.success) throw new Error(res.data.error || 'Erro ao alterar estado');
  }

  async getReport(params?: {
    start_date?: string;
    end_date?: string;
    campaign_id?: number;
    merchant_id?: number;
    estado?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: CashbackRecord[]; pagination: { total: number } }> {
    const qs = new URLSearchParams();
    if (params?.start_date) qs.append('start_date', params.start_date);
    if (params?.end_date) qs.append('end_date', params.end_date);
    if (params?.campaign_id) qs.append('campaign_id', String(params.campaign_id));
    if (params?.merchant_id) qs.append('merchant_id', String(params.merchant_id));
    if (params?.estado) qs.append('estado', params.estado);
    if (params?.limit) qs.append('limit', String(params.limit));
    if (params?.offset) qs.append('offset', String(params.offset));
    const url = `/admin/cashback/report${qs.toString() ? `?${qs}` : ''}`;
    const res = await api.get<{ success: boolean; data: CashbackRecord[]; pagination: { total: number } }>(url);
    return { data: res.data.data || [], pagination: res.data.pagination || { total: 0 } };
  }

  getExportUrl(params?: { start_date?: string; end_date?: string; campaign_id?: number }): string {
    const qs = new URLSearchParams();
    if (params?.start_date) qs.append('start_date', params.start_date);
    if (params?.end_date) qs.append('end_date', params.end_date);
    if (params?.campaign_id) qs.append('campaign_id', String(params.campaign_id));
    return `/admin/cashback/export${qs.toString() ? `?${qs}` : ''}`;
  }

  async setupTables(): Promise<void> {
    await api.post('/admin/cashback/setup-tables');
  }

  async seedCampaigns(): Promise<void> {
    const res = await api.post<{ success: boolean; error?: string; message?: string }>('/admin/cashback/seed');
    if (!res.data.success) throw new Error(res.data.error || 'Erro ao fazer seed');
  }
}

const cashbackService = new CashbackService();
export default cashbackService;
